/**
 * 用語詳細で、種別と値のタイプからその欄を出すかを決める。
 *
 * 書けない欄を「未設定」と並べると、書き漏れと見分けがつかない。欄の必須と書けないの
 * 組み合わせは設計書 nodes-edges §1 の Term の表が正で、ここはその判定を画面の側で引くだけ。
 * 種別か値のタイプが未設定の古い用語は判定できないので、欄を隠さずに全部出す。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 用語の種別と値のタイプから、用語詳細に出す欄を決める。
 * @graph-connects none
 */

/** 種別と値のタイプで出し分ける欄。 @graph-connects none */
export type TermShapeField = 'representation' | 'ownerClass' | 'unit' | 'values' | 'transitions';

/** 出し分けの判定に使う用語の形。 @graph-connects none */
export interface TermShape {
  section: string | null;
  representation: string | null;
}

/** 取りうる値を持つ値のタイプ。 @graph-connects none */
const VALUE_REPRESENTATIONS: ReadonlySet<string> = new Set(['code', 'state', 'indicator']);

/** 単位を持つ値のタイプ。 @graph-connects none */
const UNIT_REPRESENTATIONS: ReadonlySet<string> = new Set([
  'quantity',
  'measure',
  'amount',
  'dateTime',
]);

/** 出し分けの対象の欄の一覧。 @graph-connects none */
const SHAPE_FIELDS: ReadonlySet<string> = new Set([
  'representation',
  'ownerClass',
  'unit',
  'values',
  'transitions',
]);

/**
 * 欄の鍵が、種別と値のタイプで出し分ける欄かを返す。
 *
 * @param key 欄の鍵
 * @returns 出し分けの対象なら true
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 用語詳細の欄が出し分けの対象かを判定する。
 * @graph-connects none
 */
export function isTermShapeField(key: string): key is TermShapeField {
  return SHAPE_FIELDS.has(key);
}

/**
 * その用語の形で、欄を出すかを返す。
 *
 * @param field 出し分ける欄
 * @param shape 用語の種別と値のタイプ
 * @returns 出すなら true
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 用語の種別と値のタイプから、欄が書ける（出す）かを判定する。
 * @graph-connects none
 */
export function termFieldApplies(field: TermShapeField, shape: TermShape): boolean {
  if (shape.section === null) return true;
  if (shape.section !== 'property') return false;
  if (field === 'representation' || field === 'ownerClass') return true;
  if (shape.representation === null) return true;
  if (field === 'values') return VALUE_REPRESENTATIONS.has(shape.representation);
  if (field === 'unit') return UNIT_REPRESENTATIONS.has(shape.representation);
  return shape.representation === 'state';
}
